import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

/**
 * Analytics aggregates for /analytics (admin/executive/superadmin per
 * App.tsx — all three hold SELECT on bookings, staff, and hubs, so the
 * joins below are within grants).
 *
 * "Revenue" here is Σ gross_amount of COMPLETED bookings — the value of
 * sessions actually delivered. Settled-payment truth stays with the
 * PayMongo webhook (security.md); this is operational reporting, not the
 * ledger. Aggregated client-side over one bounded select — fine at pilot
 * volume, revisit as a SQL aggregate view when a month outgrows one page.
 */

export interface AnalyticsSummary {
  totalBookings: number;
  totalRevenue: number;
  averageBookingValue: number;
  topTherapists: { name: string; bookings: number; revenue: number }[];
  hubPerformance: { name: string; bookings: number; revenue: number }[];
  /** Most-booked service variants, priced from the booking-time gross amount. */
  massagePerformance: {
    name: string;
    bookings: number;
    averageBookedPrice: number;
    bookedValue: number;
  }[];
  /** A stable data key and readable name for each hub on the earnings chart. */
  hubEarningsSeries: { key: string; name: string }[];
  paymentBreakdown: { method: string; count: number; percentage: number }[];
  /** One point per day across the selected range (gaps filled with 0) —
   * feeds the booking-volume line chart. `date` is a real Date object
   * (the Bklit time-series LineChart requires Date x-values). */
  bookingsByDay: {
    date: Date;
    bookings: number;
    cancelledBookings: number;
    revenue: number;
  }[];
  /** Completed-session value per hub per day. Each dynamic hub key is numeric. */
  hubEarningsByDay: ({ date: Date } & Record<string, number | Date>)[];
}

export const EMPTY_SUMMARY: AnalyticsSummary = {
  totalBookings: 0,
  totalRevenue: 0,
  averageBookingValue: 0,
  topTherapists: [],
  hubPerformance: [],
  massagePerformance: [],
  hubEarningsSeries: [],
  paymentBreakdown: [],
  bookingsByDay: [],
  hubEarningsByDay: [],
};

export function useAnalyticsSummary(hubId: string | null, days: number) {
  return useQuery({
    queryKey: ["analytics-summary", hubId, days],
    queryFn: async (): Promise<AnalyticsSummary> => {
      const since = new Date();
      since.setDate(since.getDate() - (days - 1));
      since.setHours(0, 0, 0, 0);
      const until = new Date();
      until.setHours(0, 0, 0, 0);
      until.setDate(until.getDate() + 1);

      let query = supabase
        .from("bookings")
        .select(
          `id, status, payment_method, gross_amount, scheduled_at, hub_id,
           hubs ( name ),
           service_variants ( duration_minutes, services ( name ) ),
           therapist:staff!bookings_therapist_id_fkey ( full_name )`,
        )
        .gte("scheduled_at", since.toISOString())
        .lt("scheduled_at", until.toISOString())
        .limit(5000);
      if (hubId) query = query.eq("hub_id", hubId);

      const { data, error } = await query;
      if (error) throw error;
      const rows = data ?? [];

      const nonCancelled = rows.filter((b) => b.status !== "cancelled");
      const completed = rows.filter((b) => b.status === "completed");

      const totalRevenue = completed.reduce((sum, b) => sum + b.gross_amount, 0);
      const bookedValue = nonCancelled.reduce((sum, b) => sum + b.gross_amount, 0);

      const therapistAgg = new Map<string, { bookings: number; revenue: number }>();
      for (const b of completed) {
        const name = b.therapist?.full_name;
        if (!name) continue;
        const agg = therapistAgg.get(name) ?? { bookings: 0, revenue: 0 };
        agg.bookings += 1;
        agg.revenue += b.gross_amount;
        therapistAgg.set(name, agg);
      }

      const hubAgg = new Map<string, { bookings: number; revenue: number }>();
      for (const b of nonCancelled) {
        const name = b.hubs?.name ?? "Unassigned";
        const agg = hubAgg.get(name) ?? { bookings: 0, revenue: 0 };
        agg.bookings += 1;
        if (b.status === "completed") agg.revenue += b.gross_amount;
        hubAgg.set(name, agg);
      }

      // Use each booking's gross_amount rather than the current catalog price:
      // catalog prices may change after a booking is made, while gross_amount
      // remains the booking-time value that operations needs to analyse.
      const massageAgg = new Map<string, { bookings: number; bookedValue: number }>();
      for (const b of nonCancelled) {
        const serviceName = b.service_variants?.services?.name;
        if (!serviceName) continue;
        const duration = b.service_variants.duration_minutes;
        const name = duration ? `${serviceName} · ${duration} min` : serviceName;
        const agg = massageAgg.get(name) ?? { bookings: 0, bookedValue: 0 };
        agg.bookings += 1;
        agg.bookedValue += b.gross_amount;
        massageAgg.set(name, agg);
      }

      const paymentAgg = new Map<string, number>();
      for (const b of nonCancelled) {
        paymentAgg.set(b.payment_method, (paymentAgg.get(b.payment_method) ?? 0) + 1);
      }

      // Daily buckets keyed by local YYYY-MM-DD, then materialised for every
      // day in [since, today] so the line has no gaps at low volume.
      const dayKey = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
          d.getDate(),
        ).padStart(2, "0")}`;
      const dayAgg = new Map<
        string,
        { bookings: number; cancelledBookings: number; revenue: number }
      >();
      const hubEarningsAgg = new Map<string, Map<string, number>>();
      for (const b of rows) {
        const key = dayKey(new Date(b.scheduled_at));
        const agg = dayAgg.get(key) ?? { bookings: 0, cancelledBookings: 0, revenue: 0 };
        if (b.status === "cancelled") {
          agg.cancelledBookings += 1;
        } else {
          agg.bookings += 1;
          if (b.status === "completed") agg.revenue += b.gross_amount;
        }
        dayAgg.set(key, agg);
      }
      for (const b of completed) {
        const date = dayKey(new Date(b.scheduled_at));
        const hubName = b.hubs?.name ?? "Unassigned";
        const earningsForDay = hubEarningsAgg.get(date) ?? new Map<string, number>();
        earningsForDay.set(hubName, (earningsForDay.get(hubName) ?? 0) + b.gross_amount);
        hubEarningsAgg.set(date, earningsForDay);
      }
      const bookingsByDay: AnalyticsSummary["bookingsByDay"] = [];
      const hubEarningsSeries = [...hubAgg.entries()]
        .map(([name, agg], index) => ({
          key: `hub_${index}`,
          name,
          bookings: agg.bookings,
        }))
        .sort((a, b) => b.bookings - a.bookings)
        .map(({ key, name }) => ({ key, name }));
      const hubEarningsByDay: AnalyticsSummary["hubEarningsByDay"] = [];
      for (let i = 0; i < days; i++) {
        const d = new Date(since);
        d.setDate(since.getDate() + i);
        const agg = dayAgg.get(dayKey(d)) ?? { bookings: 0, cancelledBookings: 0, revenue: 0 };
        bookingsByDay.push({
          date: d,
          bookings: agg.bookings,
          cancelledBookings: agg.cancelledBookings,
          revenue: agg.revenue,
        });
        const earningsForDay = hubEarningsAgg.get(dayKey(d));
        const earningsByHub: AnalyticsSummary["hubEarningsByDay"][number] = { date: d };
        for (const series of hubEarningsSeries) {
          earningsByHub[series.key] = earningsForDay?.get(series.name) ?? 0;
        }
        hubEarningsByDay.push(earningsByHub);
      }

      return {
        totalBookings: nonCancelled.length,
        totalRevenue,
        averageBookingValue:
          nonCancelled.length > 0 ? Math.round((bookedValue / nonCancelled.length) * 100) / 100 : 0,
        topTherapists: [...therapistAgg.entries()]
          .map(([name, agg]) => ({ name, ...agg }))
          .sort((a, b) => b.revenue - a.revenue)
          .slice(0, 10),
        hubPerformance: [...hubAgg.entries()]
          .map(([name, agg]) => ({ name, ...agg }))
          .sort((a, b) => b.bookings - a.bookings),
        massagePerformance: [...massageAgg.entries()]
          .map(([name, agg]) => ({
            name,
            ...agg,
            averageBookedPrice: Math.round((agg.bookedValue / agg.bookings) * 100) / 100,
          }))
          .sort((a, b) => b.bookings - a.bookings || b.bookedValue - a.bookedValue)
          .slice(0, 8),
        hubEarningsSeries,
        paymentBreakdown: [...paymentAgg.entries()]
          .map(([method, count]) => ({
            method: method.replace(/_/g, " "),
            count,
            percentage:
              nonCancelled.length > 0 ? Math.round((count / nonCancelled.length) * 1000) / 10 : 0,
          }))
          .sort((a, b) => b.count - a.count),
        bookingsByDay,
        hubEarningsByDay,
      };
    },
    staleTime: 5 * 60_000,
  });
}

/** Client-side CSV of the summary tables (plain download, no PII beyond staff names). */
export function summaryToCsv(summary: AnalyticsSummary): string {
  const lines: string[] = [];
  const esc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;

  lines.push("Top Therapists");
  lines.push("Name,Bookings,Revenue");
  for (const t of summary.topTherapists) lines.push([esc(t.name), t.bookings, t.revenue].join(","));
  lines.push("");
  lines.push("Hub Performance");
  lines.push("Hub,Bookings,Revenue");
  for (const h of summary.hubPerformance)
    lines.push([esc(h.name), h.bookings, h.revenue].join(","));
  lines.push("");
  lines.push("Massage Performance");
  lines.push("Massage Type,Bookings,Average Booked Price,Booked Value");
  for (const massage of summary.massagePerformance)
    lines.push(
      [esc(massage.name), massage.bookings, massage.averageBookedPrice, massage.bookedValue].join(
        ",",
      ),
    );
  lines.push("");
  lines.push("Payment Breakdown");
  lines.push("Method,Count,Percentage");
  for (const p of summary.paymentBreakdown)
    lines.push([esc(p.method), p.count, `${p.percentage}%`].join(","));

  return lines.join("\n");
}
